/**
 * Client-side ranking primitives. The server-side source of truth is the
 * get_referrer_rank_scores() RPC (migration 026); these helpers mirror its
 * Bayesian smoothing so the app still ranks sensibly when the RPC is
 * unavailable (e.g. migration not applied yet) or for lists that never touch
 * the server.
 */

export const RATING_PRIOR = 3.8;
export const RATING_PRIOR_WEIGHT = 5;

/**
 * Bayesian-smoothed average rating: pulls low-sample averages toward the
 * platform prior, so a 5.0 with 2 reviews no longer outranks a 4.8 with 90.
 * Returns a value on the original 0–5 scale.
 */
export function bayesianRating(
  averageRating: number | null | undefined,
  reviewsCount: number | null | undefined,
  prior: number = RATING_PRIOR,
  priorWeight: number = RATING_PRIOR_WEIGHT
): number {
  const n = Math.max(0, reviewsCount ?? 0);
  const avg = averageRating ?? 0;
  return (avg * n + prior * priorWeight) / (n + priorWeight);
}

/**
 * 0..1 fallback rank score used when the server-computed rank_score is absent.
 * Rating-only (the server score additionally folds in response rate,
 * completion rate, recency, and conversion).
 */
export function fallbackRankScore(
  averageRating: number | null | undefined,
  reviewsCount: number | null | undefined
): number {
  return bayesianRating(averageRating, reviewsCount) / 5;
}
